import { supabase } from '../lib/supabase';
import { createLogger } from '../lib/logger';

const logger = createLogger('AuditService');

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  | 'export'
  | 'approve'
  | 'reject'
  | 'sign'
  | 'submit'
  | 'withdraw'
  | 'complete'
  | 'assign'
  | 'archive'
  | 'login'
  | 'logout'
  | 'password_reset_request'
  | 'password_reset'
  | 'ai_generation'
  | 'topup'
  | 'deduct'
  | 'refund'
  | 'check';

export type ResourceType =
  | 'policy'
  | 'consent'
  | 'breach'
  | 'access_request'
  | 'dpia'
  | 'dpia_risk'
  | 'dpia_mitigation'
  | 'dpmp'
  | 'user'
  | 'processing_activity'
  | 'training'
  | 'training_assignment'
  | 'cross_border_transfer'
  | 'dpo'
  | 'retention_schedule'
  | 'retention_alert'
  | 'evidence'
  | 'notification'
  | 'authority'
  | 'organisation'
  | 'action_item'
  | 'ai_generation'
  | 'template'
  | 'credit'
  | 'credit_transaction'
  | 'data_processor';

export interface AuditLogEntry {
  id?: string;
  organisation_id: string;
  user_id: string | null;
  action: AuditAction;
  resource_type: ResourceType;
  resource_id: string;
  details?: Record<string, unknown>;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at?: string;
}

export interface AuditLogFilters {
  organisationId: string;
  resourceType?: ResourceType;
  resourceId?: string;
  userId?: string;
  action?: AuditAction;
  fromDate?: string;
  toDate?: string;
}

async function getClientIp(): Promise<string | null> {
  try {
    if (typeof window === 'undefined') return null;

    const cached = sessionStorage.getItem('client_ip');
    if (cached) return cached;

    const res = await fetch('https://api.ipify.org?format=json');
    if (!res.ok) return null;

    const data = await res.json();
    const ip = data?.ip ?? null;

    if (ip) {
      sessionStorage.setItem('client_ip', ip);
    }

    return ip;
  } catch (error) {
    logger.error('Error getting client IP:', error);
    return null;
  }
}

/**
 * Low-level helper that actually inserts into the audit_log table.
 * (Renamed from createAuditLog to avoid clashing with the exported wrapper.)
 */
async function insertAuditLog(entry: AuditLogEntry): Promise<void> {
  try {
    const finalEntry: AuditLogEntry = {
      ...entry,
      created_at: entry.created_at ?? new Date().toISOString(),
      details: entry.details ?? {},
      changes: entry.changes ?? {},
      metadata: entry.metadata ?? {},
    };

    const { error } = await supabase.from('audit_log').insert(finalEntry);

    if (error) {
      logger.error('Failed to create audit log entry:', error);
    }
  } catch (error) {
    logger.error('Exception creating audit log:', error);
  }
}

/**
 * Single generic logger used by most helpers below.
 */
export async function logEvent(params: {
  organisationId: string;
  userId: string | null;
  action: AuditAction;
  resourceType: ResourceType;
  resourceId: string;
  details?: Record<string, unknown>;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  ipAddress?: string | null;
  userAgent?: string | null;
}): Promise<void> {
  try {
    const {
      organisationId,
      userId,
      action,
      resourceType,
      resourceId,
      details = {},
      changes = {},
      metadata = {},
      ipAddress,
      userAgent,
    } = params;

    const finalIp = ipAddress ?? (typeof window !== 'undefined' ? await getClientIp() : null);
    const finalUserAgent =
      userAgent ?? (typeof window !== 'undefined' ? window.navigator.userAgent : null);

    const entry: AuditLogEntry = {
      organisation_id: organisationId,
      user_id: userId,
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      details,
      changes,
      metadata,
      ip_address: finalIp,
      user_agent: finalUserAgent,
      created_at: new Date().toISOString(),
    };

    await insertAuditLog(entry);
  } catch (error) {
    logger.error('Error in logEvent:', error);
  }
}

// ---------------------------------------------------------------------------
// High-level helpers used across the app
// ---------------------------------------------------------------------------

export async function logCreate(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  resourceData: object,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'create',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Created ${resourceType}`,
    },
    changes: {
      new: resourceData as Record<string, unknown>,
    },
  });
}

export async function logUpdate(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  oldData: object,
  newData: object,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'update',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Updated ${resourceType}`,
    },
    changes: {
      old: oldData as Record<string, unknown>,
      new: newData as Record<string, unknown>,
    },
  });
}

export async function logDelete(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  deletedData: object,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'delete',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Deleted ${resourceType}`,
    },
    changes: {
      old: deletedData as Record<string, unknown>,
    },
  });
}

export async function logView(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'view',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Viewed ${resourceType}`,
    },
  });
}

export async function logExport(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'export',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Exported ${resourceType} data`,
    },
  });
}

export async function logApprove(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'approve',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Approved ${resourceType}`,
    },
    changes,
  });
}

export async function logReject(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'reject',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Rejected ${resourceType}`,
    },
    changes,
  });
}

export async function logSign(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'sign',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      ...details,
      description: `Signed ${resourceType}`,
    },
  });
}

// ---------------------------------------------------------------------------
// Authentication-related logs
// ---------------------------------------------------------------------------

export async function logPasswordResetRequest(
  organisationId: string,
  userId: string | null,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'password_reset_request',
    resource_type: 'user',
    resource_id: resourceId,
    details: {
      ...details,
      description: 'Requested password reset',
    },
  });
}

export async function logPasswordReset(
  organisationId: string,
  userId: string | null,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'password_reset',
    resource_type: 'user',
    resource_id: resourceId,
    details: {
      ...details,
      description: 'Completed password reset',
    },
  });
}

export async function logLogin(
  organisationId: string,
  userId: string | null,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'login',
    resource_type: 'user',
    resource_id: resourceId,
    details: {
      ...details,
      description: 'User login',
    },
  });
}

export async function logLogout(
  organisationId: string,
  userId: string | null,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'logout',
    resource_type: 'user',
    resource_id: resourceId,
    details: {
      ...details,
      description: 'User logout',
    },
  });
}

// ---------------------------------------------------------------------------
// Domain-specific helpers
// ---------------------------------------------------------------------------

export async function logAuthorityAssignment(
  organisationId: string,
  userId: string | null,
  authorityId: string,
  details: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'assign',
    resource_type: 'authority',
    resource_id: authorityId,
    details: {
      ...details,
      description: 'Assigned authority role',
    },
    changes: {
      new: details,
    },
  });
}

export async function logAuthorityRemoval(
  organisationId: string,
  userId: string | null,
  authorityId: string,
  details: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'delete',
    resource_type: 'authority',
    resource_id: authorityId,
    details: {
      ...details,
      description: 'Removed authority role',
    },
    changes: {
      old: details,
    },
  });
}

export async function logUserUpdate(
  organisationId: string,
  userId: string | null,
  updatedUserId: string,
  oldData: object,
  newData: object,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'update',
    resource_type: 'user',
    resource_id: updatedUserId,
    details: {
      ...details,
      description: 'Updated user profile',
    },
    changes: {
      old: oldData as Record<string, unknown>,
      new: newData as Record<string, unknown>,
    },
  });
}

export async function logPolicyApproval(
  organisationId: string,
  userId: string | null,
  policyId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await logApprove(organisationId, userId, 'policy', policyId, {
    ...details,
    description: 'Approved policy',
  });
}

export async function logPolicyRejection(
  organisationId: string,
  userId: string | null,
  policyId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await logReject(organisationId, userId, 'policy', policyId, {
    ...details,
    description: 'Rejected policy',
  });
}

export async function logPolicyPublication(
  organisationId: string,
  userId: string | null,
  policyId: string,
  details?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'approve',
    resource_type: 'policy',
    resource_id: policyId,
    details: {
      ...details,
      description: 'Published policy',
    },
  });
}

export async function logDataBreachReport(
  organisationId: string,
  userId: string | null,
  breachId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'create',
    resource_type: 'breach',
    resource_id: breachId,
    details: {
      ...details,
      description: 'Reported data breach',
    },
    changes,
  });
}

export async function logConsentCapture(
  organisationId: string,
  userId: string | null,
  consentId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'create',
    resource_type: 'consent',
    resource_id: consentId,
    details: {
      ...details,
      description: 'Captured consent',
    },
    changes,
  });
}

export async function logConsentWithdrawal(
  organisationId: string,
  userId: string | null,
  consentId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'withdraw',
    resource_type: 'consent',
    resource_id: consentId,
    details: {
      ...details,
      description: 'Withdrew consent',
    },
    changes,
  });
}

export async function logAccessRequestSubmission(
  organisationId: string,
  userId: string | null,
  requestId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'submit',
    resource_type: 'access_request',
    resource_id: requestId,
    details: {
      ...details,
      description: 'Submitted access request',
    },
    changes,
  });
}

export async function logAccessRequestUpdate(
  organisationId: string,
  userId: string | null,
  requestId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'update',
    resource_type: 'access_request',
    resource_id: requestId,
    details: {
      ...details,
      description: 'Updated access request',
    },
    changes,
  });
}

export async function logAccessRequestCompletion(
  organisationId: string,
  userId: string | null,
  requestId: string,
  details?: Record<string, unknown>,
  changes?: Record<string, unknown>
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'complete',
    resource_type: 'access_request',
    resource_id: requestId,
    details: {
      ...details,
      description: 'Completed access request',
    },
    changes,
  });
}

export async function logAiGeneration(
  organisationId: string,
  userId: string | null,
  resourceType: ResourceType,
  resourceId: string,
  prompt: string,
  outputSummary?: string
): Promise<void> {
  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action: 'ai_generation',
    resource_type: resourceType,
    resource_id: resourceId,
    details: {
      description: 'AI generation',
      prompt,
      outputSummary,
    },
  });
}

// ---------------------------------------------------------------------------
// Fetch / query
// ---------------------------------------------------------------------------

export async function fetchAuditLogs(filters: AuditLogFilters) {
  const {
    organisationId,
    resourceType,
    resourceId,
    userId,
    action,
    fromDate,
    toDate,
  } = filters;

  let query = supabase
    .from('audit_log_with_users') // view that joins users for display
    .select('*')
    .eq('organisation_id', organisationId)
    .order('created_at', { ascending: false });

  if (resourceType) {
    query = query.eq('resource_type', resourceType);
  }

  if (resourceId) {
    query = query.eq('resource_id', resourceId);
  }

  if (userId) {
    query = query.eq('user_id', userId);
  }

  if (action) {
    query = query.eq('action', action);
  }

  if (fromDate) {
    query = query.gte('created_at', fromDate);
  }

  if (toDate) {
    query = query.lte('created_at', toDate);
  }

  const { data, error } = await query;

  if (error) {
    logger.error('Failed to fetch audit logs:', error);
    throw error;
  }

  return data ?? [];
}

// ---------------------------------------------------------------------------
// NEW generic helpers for newer code paths (DPIA, etc.)
// ---------------------------------------------------------------------------

export interface CreateAuditLogParams {
  organisationId: string;
  userId?: string | null;
  action: AuditAction;
  resourceType: ResourceType;
  resourceId: string;
  details?: Record<string, unknown>;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Generic function that other services can call directly.
 * (Used by userManagementService, DPIA manager, etc.)
 */
export async function createAuditLog(params: CreateAuditLogParams): Promise<void> {
  const {
    organisationId,
    userId = null,
    action,
    resourceType,
    resourceId,
    details = {},
    changes = {},
    metadata = {},
  } = params;

  await insertAuditLog({
    organisation_id: organisationId,
    user_id: userId,
    action,
    resource_type: resourceType,
    resource_id: resourceId,
    details,
    changes,
    metadata,
    ip_address: null,
    user_agent: null,
    created_at: new Date().toISOString(),
  });
}

/**
 * Alias used by some modules (e.g. DPIA manager). Simply forwards to createAuditLog.
 */
export async function logAuditEvent(params: CreateAuditLogParams): Promise<void> {
  return createAuditLog(params);
}
